import React, { useEffect, useRef } from 'react';

const CodeVisualizer = () => {
    const canvasRef = useRef(null);
    const color = "#FF00ED"; // Neon Magenta

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let animationFrameId;

        const resize = () => {
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
        };

        window.addEventListener('resize', resize);
        resize();

        // Matrix code state
        const columns = Math.floor(canvas.width / 20);
        const drops = new Array(columns).fill(0);
        const chars = "10<>{}[]/\\_=+*!#&^%@$";

        // AI Core state
        const coreSize = 60;
        let corePulse = 0;

        // Neural particles
        const particles = [];
        for (let i = 0; i < 40; i++) {
            particles.push({
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height,
                vx: (Math.random() - 0.5) * 1.5,
                vy: (Math.random() - 0.5) * 1.5,
                life: Math.random()
            });
        }

        const draw = () => {
            ctx.fillStyle = 'rgba(1, 1, 8, 0.15)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            const cx = canvas.width / 2;
            const cy = canvas.height / 2;

            // 1. Draw Background Coding Matrix
            ctx.fillStyle = color;
            ctx.font = '9px monospace';
            ctx.globalAlpha = 0.15;
            for (let i = 0; i < drops.length; i++) {
                const text = chars[Math.floor(Math.random() * chars.length)];
                ctx.fillText(text, i * 20, drops[i] * 20);
                if (drops[i] * 20 > canvas.height && Math.random() > 0.98) drops[i] = 0;
                drops[i]++;
            }

            // 2. Draw Circuit Connections
            ctx.globalAlpha = 0.3;
            ctx.strokeStyle = color;
            ctx.lineWidth = 1;
            particles.forEach((p, i) => {
                p.x += p.vx;
                p.y += p.vy;
                if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
                if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

                // Move towards center sometimes (AI pull)
                const dx = cx - p.x;
                const dy = cy - p.y;
                const dist = Math.sqrt(dx*dx + dy*dy);
                if (dist < 200) {
                    p.vx += dx * 0.0001;
                    p.vy += dy * 0.0001;
                }

                ctx.beginPath();
                ctx.arc(p.x, p.y, 1.5, 0, Math.PI * 2);
                ctx.fill();

                // Connect logic
                particles.slice(i + 1).forEach(p2 => {
                    const d = Math.sqrt((p.x-p2.x)**2 + (p.y-p2.y)**2);
                    if (d < 80) {
                        ctx.beginPath();
                        ctx.moveTo(p.x, p.y);
                        ctx.lineTo(p2.x, p2.y);
                        ctx.globalAlpha = (1 - d/80) * 0.2;
                        ctx.stroke();
                    }
                });
            });

            // 3. Draw AI Neural Core (The Pulsing Heart)
            corePulse += 0.05;
            const pulseScale = 1 + Math.sin(corePulse) * 0.1;
            
            ctx.globalAlpha = 1;
            ctx.shadowBlur = 20 * pulseScale;
            ctx.shadowColor = color;
            
            // Outer Ring
            ctx.beginPath();
            ctx.arc(cx, cy, coreSize * pulseScale, 0, Math.PI * 2);
            ctx.strokeStyle = color;
            ctx.lineWidth = 2;
            ctx.stroke();

            // Inner Hexagon (AI Logic)
            ctx.beginPath();
            for(let i=0; i<6; i++) {
                const angle = i * Math.PI / 3 + corePulse * 0.2;
                const x = cx + (coreSize * 0.6 * pulseScale) * Math.cos(angle);
                const y = cy + (coreSize * 0.6 * pulseScale) * Math.sin(angle);
                if (i === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
            }
            ctx.closePath();
            ctx.fillStyle = `${color}20`;
            ctx.fill();
            ctx.stroke();

            // Data spikes
            ctx.lineWidth = 0.5;
            for(let i=0; i<8; i++) {
                const a = i * Math.PI / 4 - corePulse * 0.5;
                ctx.beginPath();
                ctx.moveTo(cx + coreSize * Math.cos(a), cy + coreSize * Math.sin(a));
                ctx.lineTo(cx + (coreSize + 30) * Math.cos(a), cy + (coreSize + 30) * Math.sin(a));
                ctx.stroke();
            }

            ctx.shadowBlur = 0;
            animationFrameId = requestAnimationFrame(draw);
        };

        draw();

        return () => {
            window.removeEventListener('resize', resize);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <canvas 
                ref={canvasRef} 
                style={{ width: '100%', height: '100%' }}
            />
            {/* Top HUD */}
            <div style={{
                position: 'absolute', top: '5%', right: '5%',
                fontFamily: 'monospace', color: color, fontSize: '9px',
                textAlign: 'right', borderRight: `2px solid ${color}`, paddingRight: '10px'
            }}>
                AI_INTEGRATION_SYNC: 99.2%<br/>
                NEURAL_LINK: ESTABLISHED<br/>
                MODEL: GPT-4_OSINT_V2
            </div>
            {/* Bottom HUD */}
            <div style={{
                position: 'absolute', bottom: '5%', left: '5%',
                fontFamily: 'monospace', color: color, fontSize: '9px',
                borderLeft: `2px solid ${color}`, paddingLeft: '10px'
            }}>
                CORE_TEMP: 32°C<br/>
                AUTO_SCRIPTS: 1,248_ACTIVE<br/>
                SYS_ORCHESTRATOR: ONLINE
            </div>
        </div>
    );
};

export default CodeVisualizer;
